import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Search, X, ShoppingCart, Loader, Package, Wrench, Plus, Minus, FileText, Image } from 'lucide-react';

/**
 * QuickQuoteModal — Unified product search + service picker for quick quoting.
 * Searches both Polytech and Apple Argentina, lets agent build a quote,
 * and inserts formatted text into the ticket reply.
 *
 * Props:
 *   onClose: () => void
 *   onInsert: (text: string, items: array) => void — inserts quote text into reply
 *   onCreateBudget: (items: array, services: array) => void — creates formal budget
 *   channel: 'whatsapp' | 'email' — affects formatting
 */

const SERVICE_TEMPLATES = [
  { id: 'diagnosis', label: 'Diagnóstico', price: 50 },
  { id: 'setup', label: 'Configuración', price: 80 },
  { id: 'installation', label: 'Instalación', price: 100 },
  { id: 'data_migration', label: 'Migración de datos', price: 100 },
  { id: 'onsite', label: 'Visita técnica', price: 80 },
  { id: 'format', label: 'Formateo / Reinstalación de OS', price: 90 },
  { id: 'pc_assembly', label: 'Armado de PC', price: 90 },
  { id: 'backup', label: 'Backup y restauración', price: 90 },
  { id: 'maintenance', label: 'Mantenimiento preventivo', price: 65 },
  { id: 'custom', label: 'Otro servicio...', price: 0 },
];

export default function QuickQuoteModal({ onClose, onInsert, onCreateBudget, channel = 'email', ticketId }) {
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('all'); // 'all' | 'polytech' | 'appleargentina'
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cart, setCart] = useState([]); // [{ ...item, qty, supplier }]
  const [services, setServices] = useState([]); // [{ id, label, price }]
  const [customServiceLabel, setCustomServiceLabel] = useState('');
  const [customServicePrice, setCustomServicePrice] = useState('');
  const [sendingImages, setSendingImages] = useState(false);
  const [blueDolar, setBlueDolar] = useState(null);
  const debounceRef = useRef(null);

  // Fetch blue dollar rate on mount
  useEffect(() => {
    fetch('https://dolarapi.com/v1/dolares/blue')
      .then(r => r.json())
      .then(data => setBlueDolar(data.venta ? data.venta + 5 : null))
      .catch(() => setBlueDolar(null));
  }, []);

  const doSearch = useCallback(async (q, p = 1, sup = supplier) => {
    if (!q || q.trim().length < 2) { setResults([]); setTotalPages(1); return; }
    setLoading(true);
    try {
      const searches = [];
      if (sup === 'all' || sup === 'polytech') {
        searches.push(
          api.get('/polytech/search', { params: { q: q.trim(), page: p } })
            .then(r => (r.data.items || []).map(i => ({ ...i, _supplier: 'Polytech' })))
            .catch(() => [])
        );
      }
      if (sup === 'all' || sup === 'appleargentina') {
        searches.push(
          api.get('/appleargentina/search', { params: { q: q.trim(), page: p } })
            .then(r => (r.data.items || []).map(i => ({ ...i, _supplier: 'Apple AR' })))
            .catch(() => [])
        );
      }
      const allResults = (await Promise.all(searches)).flat();
      // Sort: in-stock first
      allResults.sort((a, b) => (b.stock > 0 ? 1 : 0) - (a.stock > 0 ? 1 : 0));
      setResults(allResults);
      setPage(p);
      setTotalPages(1); // unified search doesn't paginate well across suppliers
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, [supplier]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(search, 1, supplier), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, supplier, doSearch]);

  const addToCart = (item) => {
    setCart(prev => {
      const key = `${item._supplier}:${item.source_id}`;
      const existing = prev.find(c => `${c._supplier}:${c.source_id}` === key);
      if (existing) return prev.map(c => `${c._supplier}:${c.source_id}` === key ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { ...item, qty: 1 }];
    });
    toast.success(`${item.title} agregado`);
  };

  const updateQty = (item, qty) => {
    const key = `${item._supplier}:${item.source_id}`;
    if (qty <= 0) setCart(prev => prev.filter(c => `${c._supplier}:${c.source_id}` !== key));
    else setCart(prev => prev.map(c => `${c._supplier}:${c.source_id}` === key ? { ...c, qty } : c));
  };

  const removeFromCart = (item) => {
    const key = `${item._supplier}:${item.source_id}`;
    setCart(prev => prev.filter(c => `${c._supplier}:${c.source_id}` !== key));
  };

  const addService = (tpl) => {
    if (tpl.id === 'custom') return; // handled separately
    if (services.find(s => s.id === tpl.id)) return;
    setServices(prev => [...prev, { ...tpl }]);
  };

  const addCustomService = () => {
    if (!customServiceLabel.trim()) return;
    const id = `custom_${Date.now()}`;
    setServices(prev => [...prev, { id, label: customServiceLabel.trim(), price: parseFloat(customServicePrice) || 0 }]);
    setCustomServiceLabel('');
    setCustomServicePrice('');
  };

  const updateServicePrice = (id, price) => {
    setServices(prev => prev.map(s => s.id === id ? { ...s, price: parseFloat(price) || 0 } : s));
  };

  const removeService = (id) => {
    setServices(prev => prev.filter(s => s.id !== id));
  };

  const hasAppleOnly = cart.length > 0 && cart.every(c => c._supplier === 'Apple AR') && services.length === 0;
  const productsTotal = cart.reduce((sum, c) => sum + c.price * c.qty, 0);
  const servicesTotal = services.reduce((sum, s) => sum + s.price, 0);
  const servicesTotalARS = blueDolar ? Math.round(servicesTotal * blueDolar) : null;
  const grandTotal = productsTotal + servicesTotal;
  const grandTotalARS = blueDolar ? Math.round(grandTotal * blueDolar) : null;

  const stockLabel = (item) => {
    if (item.stock_status) {
      if (item.stock_status === 'in_stock') return 'En stock';
      if (item.stock_status === 'incoming') return 'En camino';
      return 'No disponible';
    }
    return item.stock > 0 ? `${item.stock} en stock` : 'Sin stock';
  };

  const stockColor = (item) => {
    if (item.stock_status === 'incoming') return 'var(--c-warning, #f59e0b)';
    if (item.stock > 0 || item.stock_status === 'in_stock') return 'var(--c-success, #22c55e)';
    return 'var(--c-danger, #ef4444)';
  };

  // Format quote as plain text (for WhatsApp) or HTML (for email)
  const formatQuote = () => {
    const isWA = channel === 'whatsapp';
    let lines = [];
    const fmtARS = (v) => `$${Math.round(v).toLocaleString('es-AR')}`;

    if (cart.length > 0) {
      if (isWA) lines.push('*Cotización de productos:*\n');
      cart.forEach(c => {
        const line = `${c.qty > 1 ? c.qty + 'x ' : ''}${c.title} — USD ${(c.price * c.qty).toFixed(2)}`;
        lines.push(`• ${line}`);
      });
    }

    if (services.length > 0) {
      if (isWA) lines.push('\n*Servicios:*');
      else if (cart.length > 0) lines.push('');
      services.forEach(s => {
        const arsPrice = blueDolar ? ` (${fmtARS(s.price * blueDolar)})` : '';
        lines.push(`• ${s.label} — USD ${s.price.toFixed(2)}${arsPrice}`);
      });
    }

    lines.push('');
    // Total line — show ARS equivalent for services, keep Apple products in USD only
    if (hasAppleOnly) {
      lines.push(isWA ? `*Total: USD ${grandTotal.toFixed(2)}*` : `Total: USD ${grandTotal.toFixed(2)}`);
    } else if (cart.length === 0 && services.length > 0 && servicesTotalARS) {
      // Services only — show in pesos
      lines.push(isWA ? `*Total: ${fmtARS(servicesTotalARS)}*` : `Total: ${fmtARS(servicesTotalARS)}`);
    } else {
      // Mixed or products-only
      const arsNote = grandTotalARS ? ` (${fmtARS(grandTotalARS)})` : '';
      lines.push(isWA ? `*Total: USD ${grandTotal.toFixed(2)}${arsNote}*` : `Total: USD ${grandTotal.toFixed(2)}${arsNote}`);
    }

    // Payment terms per supplier
    const hasPolytech = cart.some(c => c._supplier === 'Polytech');
    const hasAA = cart.some(c => c._supplier === 'Apple AR');

    if (hasPolytech || hasAA) {
      lines.push('');
      if (isWA) lines.push('*Condiciones:*');
    }
    if (hasPolytech) {
      lines.push(isWA
        ? '• Equipamiento Polytech: Se factura con Factura A (IVA incluido). Pago por adelantado. Se requiere CUIT o DNI para facturar.'
        : '• Equipamiento Polytech: Se factura con Factura A (IVA incluido). Pago por adelantado. Se requiere CUIT o DNI para facturar.');
    }
    if (hasAA) {
      lines.push(isWA
        ? '• Productos Apple: Precio en dólares, pago en efectivo al momento de retiro en nuestra oficina de Belgrano.'
        : '• Productos Apple: Precio en dólares, pago en efectivo al momento de retiro en nuestra oficina de Belgrano.');
    }

    lines.push('');
    lines.push(isWA
      ? '_Precios y stock válidos por 24hs. Pasado ese plazo, se debe reconfirmar disponibilidad._'
      : 'Precios y stock válidos por 24hs. Pasado ese plazo, se debe reconfirmar disponibilidad.');

    if (isWA) {
      return lines.join('\n');
    }

    // HTML for email — with product images
    let html = '<div style="padding:16px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;font-size:14px;line-height:1.6">';
    if (cart.length > 0) {
      html += '<p style="margin:0 0 12px;font-weight:600">Cotización de productos:</p>';
      cart.forEach(c => {
        const imgHtml = c.image_url
          ? `<img src="${c.image_url}" alt="" style="width:44px;height:44px;object-fit:contain;border-radius:6px;background:#fff;border:1px solid #e5e7eb" />`
          : '<div style="width:44px;height:44px;background:#f3f4f6;border-radius:6px;border:1px solid #e5e7eb"></div>';
        html += `<div style="display:flex;align-items:center;gap:12px;padding:8px 0;border-bottom:1px solid #f3f4f6">`;
        html += imgHtml;
        html += `<div style="flex:1">`;
        html += `<div style="font-weight:500">${c.qty > 1 ? c.qty + 'x ' : ''}${c.title}</div>`;
        html += `<div style="font-size:12px;color:#6b7280">${c._supplier}${c.stock_label ? ' · ' + c.stock_label : ''}</div>`;
        html += `</div>`;
        html += `<div style="font-weight:700;color:#2563eb;white-space:nowrap">USD ${(c.price * c.qty).toFixed(2)}</div>`;
        html += `</div>`;
      });
      html += '<div style="height:8px"></div>';
    }
    if (services.length > 0) {
      html += '<p style="margin:0 0 8px;font-weight:600">Servicios:</p>';
      html += '<ul style="margin:0 0 12px;padding-left:20px">';
      services.forEach(s => {
        const arsPrice = blueDolar ? ` <span style="color:#6b7280">(${fmtARS(s.price * blueDolar)})</span>` : '';
        html += `<li>${s.label} — <strong>USD ${s.price.toFixed(2)}</strong>${arsPrice}</li>`;
      });
      html += '</ul>';
    }
    // Total
    let totalHtml;
    if (hasAppleOnly) {
      totalHtml = `Total: USD ${grandTotal.toFixed(2)}`;
    } else if (cart.length === 0 && services.length > 0 && servicesTotalARS) {
      totalHtml = `Total: ${fmtARS(servicesTotalARS)}`;
    } else {
      const arsNote = grandTotalARS ? ` <span style="color:#6b7280">(${fmtARS(grandTotalARS)})</span>` : '';
      totalHtml = `Total: USD ${grandTotal.toFixed(2)}${arsNote}`;
    }
    html += `<p style="margin:12px 0 4px;font-size:16px;font-weight:700">${totalHtml}</p>`;

    const hasPolytechE = cart.some(c => c._supplier === 'Polytech');
    const hasAAE = cart.some(c => c._supplier === 'Apple AR');
    if (hasPolytechE || hasAAE) {
      html += '<p style="margin:8px 0 4px;font-weight:600;font-size:13px">Condiciones:</p><ul style="margin:0;padding-left:20px;font-size:12px;color:#4b5563">';
      if (hasPolytechE) html += '<li>Equipamiento Polytech: Factura A (IVA incluido). Pago por adelantado. Se requiere CUIT o DNI.</li>';
      if (hasAAE) html += '<li>Productos Apple: Precio en dólares, pago en efectivo al retirar en nuestra oficina de Belgrano.</li>';
      html += '</ul>';
    }
    html += '<p style="margin:8px 0 0;font-size:12px;color:#6b7280;font-style:italic">Precios y stock válidos por 24hs. Pasado ese plazo, se debe reconfirmar disponibilidad.</p>';
    html += '</div>';
    return html;
  };

  const handleInsert = () => {
    if (cart.length === 0 && services.length === 0) {
      toast.error('Agregá al menos un producto o servicio');
      return;
    }
    onInsert(formatQuote(), [...cart], [...services]);
    onClose();
  };

  const handleCreateBudget = () => {
    if (cart.length === 0 && services.length === 0) {
      toast.error('Agregá al menos un producto o servicio');
      return;
    }
    if (onCreateBudget) onCreateBudget([...cart], [...services]);
    onClose();
  };

  const handleSendImages = async () => {
    const itemsWithImages = cart.filter(c => c.image_url);
    if (!itemsWithImages.length) { toast.error('No hay imágenes para enviar'); return; }
    if (!ticketId) { toast.error('No se puede enviar sin ticket'); return; }
    setSendingImages(true);
    try {
      for (const item of itemsWithImages) {
        await api.post(`/tickets/${ticketId}/messages`, {
          body: `${item.title} — USD ${item.price.toFixed(2)}`,
          imageUrl: item.image_url,
        });
      }
      toast.success(`${itemsWithImages.length} imagen(es) enviada(s)`);
    } catch {
      toast.error('Error al enviar imágenes');
    }
    setSendingImages(false);
  };

  const hasItems = cart.length > 0 || services.length > 0;
  const hasImages = cart.some(c => c.image_url);

  const modal = (
    <div style={overlayStyle} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--c-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCart size={18} /> Cotización Rápida
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {blueDolar && <span style={{ fontSize: 10, color: 'var(--c-text-muted)', background: 'var(--c-surface-2, var(--c-bg))', padding: '2px 6px', borderRadius: 4 }}>Blue: ${blueDolar}</span>}
            <button onClick={onClose} style={closeBtnStyle}><X size={18} /></button>
          </div>
        </div>

        {/* Supplier tabs + Search */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {[
            { key: 'all', label: 'Todos' },
            { key: 'polytech', label: 'Polytech' },
            { key: 'appleargentina', label: ' Apple AR' },
          ].map(s => (
            <button key={s.key}
              onClick={() => setSupplier(s.key)}
              style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                border: supplier === s.key ? '1px solid var(--c-brand)' : '1px solid var(--c-border)',
                background: supplier === s.key ? 'rgba(59,130,246,0.1)' : 'transparent',
                color: supplier === s.key ? 'var(--c-brand)' : 'var(--c-text-muted)',
              }}>
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--c-text-muted)' }} />
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar productos..."
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 34 }}
          />
        </div>

        {/* Search Results */}
        <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 12, color: 'var(--c-text-muted)' }}>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}
          {!loading && search.length >= 2 && results.length === 0 && (
            <p style={{ color: 'var(--c-text-muted)', fontSize: 12, textAlign: 'center', padding: 12 }}>
              No se encontraron productos
            </p>
          )}
          {results.map((item, idx) => (
            <div key={`${item._supplier}-${item.source_id}-${idx}`} style={{
              padding: '8px 10px', borderBottom: '1px solid var(--c-border)',
              display: 'flex', gap: 8, alignItems: 'center',
              opacity: (item.stock <= 0 && item.stock_status !== 'incoming') ? 0.5 : 1,
            }}>
              {item.image_url && (
                <img src={item.image_url} alt="" style={{
                  width: 36, height: 36, objectFit: 'contain', borderRadius: 4,
                  background: '#fff', flexShrink: 0,
                }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 10, color: 'var(--c-text-muted)', display: 'flex', gap: 6 }}>
                  <span style={{ background: 'var(--c-surface-2, var(--c-bg))', padding: '1px 4px', borderRadius: 3 }}>{item._supplier}</span>
                  <span style={{ color: stockColor(item) }}>{stockLabel(item)}</span>
                </div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-brand)', whiteSpace: 'nowrap' }}>
                ${item.price.toFixed(2)}
              </div>
              <button onClick={() => addToCart(item)} style={{ ...actionBtnStyle, padding: '4px 8px' }}>
                <Plus size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Cart */}
        {cart.length > 0 && (
          <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={13} /> Productos ({cart.length})
            </div>
            {cart.map(ci => {
              const key = `${ci._supplier}:${ci.source_id}`;
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 12 }}>
                  <button onClick={() => removeFromCart(ci)} style={closeBtnStyle}><X size={12} /></button>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--c-text)' }}>
                    {ci.title}
                    <span style={{ color: 'var(--c-text-muted)', marginLeft: 4 }}>({ci._supplier})</span>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button onClick={() => updateQty(ci, ci.qty - 1)} style={{ ...actionBtnStyle, padding: '2px 4px' }}><Minus size={10} /></button>
                    <span style={{ width: 24, textAlign: 'center' }}>{ci.qty}</span>
                    <button onClick={() => updateQty(ci, ci.qty + 1)} style={{ ...actionBtnStyle, padding: '2px 4px' }}><Plus size={10} /></button>
                  </div>
                  <span style={{ fontWeight: 600, color: 'var(--c-brand)', minWidth: 60, textAlign: 'right' }}>
                    ${(ci.price * ci.qty).toFixed(2)}
                  </span>
                </div>
              );
            })}
            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--c-text-muted)', marginTop: 4 }}>
              Subtotal productos: <strong style={{ color: 'var(--c-text)' }}>${productsTotal.toFixed(2)}</strong>
            </div>
          </div>
        )}

        {/* Services */}
        <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Wrench size={13} /> Servicios
          </div>

          {/* Quick add service buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {SERVICE_TEMPLATES.filter(t => t.id !== 'custom').map(tpl => {
              const active = services.find(s => s.id === tpl.id);
              return (
                <button key={tpl.id}
                  onClick={() => active ? removeService(tpl.id) : addService(tpl)}
                  style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                    border: active ? '1px solid var(--c-brand)' : '1px solid var(--c-border)',
                    background: active ? 'rgba(59,130,246,0.1)' : 'transparent',
                    color: active ? 'var(--c-brand)' : 'var(--c-text-muted)',
                  }}>
                  {tpl.label} ${tpl.price}
                </button>
              );
            })}
          </div>

          {/* Custom service input */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
            <input
              value={customServiceLabel}
              onChange={e => setCustomServiceLabel(e.target.value)}
              placeholder="Otro servicio..."
              style={{ ...inputStyle, flex: 1, padding: '4px 8px', fontSize: 12 }}
            />
            <input
              value={customServicePrice}
              onChange={e => setCustomServicePrice(e.target.value)}
              placeholder="USD"
              type="number"
              min="0"
              style={{ ...inputStyle, width: 60, padding: '4px 8px', fontSize: 12 }}
            />
            <button onClick={addCustomService} style={{ ...actionBtnStyle, padding: '4px 8px' }} disabled={!customServiceLabel.trim()}>
              <Plus size={12} />
            </button>
          </div>

          {/* Selected services with editable prices */}
          {services.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 12 }}>
              <button onClick={() => removeService(s.id)} style={closeBtnStyle}><X size={12} /></button>
              <span style={{ flex: 1, color: 'var(--c-text)' }}>{s.label}</span>
              <span style={{ color: 'var(--c-text-muted)', fontSize: 11 }}>USD</span>
              <input
                type="number"
                min="0"
                value={s.price}
                onChange={e => updateServicePrice(s.id, e.target.value)}
                style={{ ...inputStyle, width: 60, padding: '3px 6px', fontSize: 12, textAlign: 'right' }}
              />
            </div>
          ))}
          {services.length > 0 && (
            <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--c-text-muted)', marginTop: 4 }}>
              Subtotal servicios: <strong style={{ color: 'var(--c-text)' }}>${servicesTotal.toFixed(2)}</strong>
              {servicesTotalARS && <span style={{ marginLeft: 6 }}>({`$${servicesTotalARS.toLocaleString('es-AR')}`})</span>}
            </div>
          )}
        </div>

        {/* Total + Actions */}
        {hasItems && (
          <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-text)' }}>
                Total: USD {grandTotal.toFixed(2)}
                {grandTotalARS && !hasAppleOnly && <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text-muted)', marginLeft: 6 }}>
                  (${grandTotalARS.toLocaleString('es-AR')})
                </span>}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {onCreateBudget && (
                <button onClick={handleCreateBudget}
                  style={{ ...actionBtnStyle, padding: '8px 16px', fontSize: 13 }}>
                  <FileText size={14} /> Crear Presupuesto
                </button>
              )}
              {channel === 'whatsapp' && hasImages && ticketId && (
                <button onClick={handleSendImages} disabled={sendingImages}
                  style={{ ...actionBtnStyle, padding: '8px 16px', fontSize: 13 }}>
                  <Image size={14} /> {sendingImages ? 'Enviando...' : 'Enviar Fotos'}
                </button>
              )}
              <button onClick={handleInsert}
                style={{ ...actionBtnStyle, padding: '8px 16px', fontSize: 13, fontWeight: 600, background: 'var(--c-brand)', color: '#fff', border: 'none' }}>
                Insertar en Respuesta
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
  display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
};

const modalStyle = {
  background: 'var(--c-surface)', borderRadius: 12, border: '1px solid var(--c-border)',
  padding: 20, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto',
};

const closeBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted)', padding: 2,
};

const inputStyle = {
  padding: '9px 12px', borderRadius: 6, border: '1px solid var(--c-border)',
  background: 'var(--c-surface)', color: 'var(--c-text)', fontSize: 14, outline: 'none',
};

const actionBtnStyle = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid var(--c-border)',
  background: 'var(--c-surface-2, var(--c-surface))', color: 'var(--c-text)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
};
